import React, { useState } from "react";
import { Button, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Spin } from "antd";
import { ToolOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import { createMaterial, updateMaterial } from "../services/materielApi";
import { useQueryClient } from "react-query";
import { uploadImage, deleteMedia } from "../../Media/services/mediaApi";
import FormField from "../../../Components/Common/Form/FormField";
import SelectField from "../../../Components/Common/Form/SelectField";
import FileField from "../../../Components/Common/Form/FileField";
import { useImageUpload } from "../../../Components/Hooks/useImageUpload";

const AddEditMaterielForm = ({ modal, setModal, toggle, selectedMateriel }) => {
  const queryClient = useQueryClient();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const { image, handlePhotoFileUpdate } = useImageUpload();

  const validationSchema = Yup.object().shape({
    // filename: Yup.string().required("Veuillez uploader la photo"),
    name: Yup.string().required("Veuillez renseigner le nom du matériel"),
    description: Yup.string().optional(),
    nbMaterial: Yup.number()
      .min(0, "Le nombre doit être positif")
      .required("Veuillez renseigner le nombre de matériel"),
    color: Yup.string().optional(),
    // filename: Yup.string().required("La photo est obligatoire"),
    // state: Yup.string().required("Veuillez renseigner l’état du matériel"),
    // status: Yup.string().required("Veuillez renseigner le statut"),
  });

  const initialValues = selectedMateriel
    ? {
        name: selectedMateriel.name || "",
        description: selectedMateriel.description || "",
        nbMaterial: selectedMateriel.nbMaterial || "",
        color: selectedMateriel.color || "",
        // type: selectedMateriel.type || "",
        filename: selectedMateriel.filename || "",
        state: selectedMateriel.state || "",
        serialNumber: selectedMateriel.serialNumber || "",
        // version: selectedMateriel.version || "",
        // category: selectedMateriel.category || "",
        status: selectedMateriel.status || "",
      }
    : {
        name: "",
        description: "",
        nbMaterial: 1,
        color: "",
        // type: "",
        filename: "",
        state: "",
        serialNumber: "",
        // version: "",
        // category: "",
        status: "DISPONIBLE",
      };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => handleSubmit(values),
    enableReinitialize: true,
  });

  const handleSubmit = async (values) => {
    setIsLoadingSubmit(true);
    let filename = values.filename;

    try {
      if (image) {
        const response = await uploadImage("materiels", image);
        filename = response?.name;
      }

      if (selectedMateriel) {
        await updateMaterial(selectedMateriel.id, { ...values, filename });
        toast.success("Matériel modifié avec succès 🎉");
      } else {
        await createMaterial({ ...values, filename });
        toast.success("Matériel ajouté avec succès 🎉");
      }

      await queryClient.invalidateQueries("materiels");
      toggle();
      formik.resetForm();
    } catch (error) {
      toast.error("Erreur : " + error?.message);
      if (image && filename) {
        try {
          await deleteMedia(filename);
        } catch (deleteError) {
          console.error("Erreur lors de la suppression de l'image :", deleteError);
        }
      }
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const stateOptions = [
    { id: "NEUF", name: "Neuf" },
    { id: "BON", name: "Bon" },
    { id: "MAUVAIS", name: "Mauvais" },
    { id: "HORS_SERVICE", name: "Hors service" },
    { id: "EN_REPARATION", name: "En réparation" },
    { id: "EN_MAINTENANCE", name: "En maintenance" },
    { id: "PERDU", name: "Perdu" },
    { id: "VOLE", name: "Volé" },
    { id: "ANCIEN", name: "Ancien" },
    { id: "EN_PANNE", name: "En panne" },
  ];
  
  return (
    <Modal isOpen={modal} toggle={toggle} centered backdrop="static" style={{ maxWidth: "800px" }}>
      <ModalHeader className="bg-info-subtle p-3" toggle={toggle}>
        {selectedMateriel ? "Édition" : "Ajout"} d’un matériel <ToolOutlined />
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <FileField 
              label="Photo du matériel" 
              name="filename" formik={formik} 
              file={image} onUpdateFiles={handlePhotoFileUpdate} 
              helperText="L’image est très recommandée" 
              colSize={12} 
            />

            <FormField label="Nom" name="name" formik={formik} colSize={6} />
            <FormField label="Nombre" name="nbMaterial" type="number" formik={formik} colSize={6} />
            <FormField label="Couleur" name="color" formik={formik} colSize={6} />
            <FormField label="Numéro de série" name="serialNumber" formik={formik} colSize={6} />
            <FormField label="Prix" name="price" formik={formik} colSize={6} />
            {/* <FormField label="Version" name="version" formik={formik} colSize={6} /> */}
            {/* <FormField label="Type" name="type" formik={formik} colSize={6} /> */}
            <SelectField label="État" name="state" formik={formik} options={stateOptions} nameSelect="l’état" colSize={6} />
            {/* <FormField label="Catégorie" name="category" formik={formik} colSize={6} /> */}
            <FormField label="Description" name="description" formik={formik} isTextarea={true} colSize={12} />
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>
            Annuler
          </Button>
          <Button type="submit" className="btn btn-soft-success" disabled={isLoadingSubmit}>
            {isLoadingSubmit ? <Spin size="small" /> : selectedMateriel ? "Modifier" : "Ajouter"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddEditMaterielForm;
